import { folderApi as api } from './folderApi';
export const addTagTypes = ['UserFolders'] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes
  })
  .injectEndpoints({
    endpoints: build => ({
      getUserFolders: build.query<
        GetUserFoldersApiResponse,
        GetUserFoldersApiArg
      >({
        query: queryArg => ({
          url: `/userFolders`,
          params: {
            search: queryArg.search,
            offset: queryArg.offset,
            limit: queryArg.limit,
            sortby: queryArg.sortby,
            orderby: queryArg.orderby,
            userId: queryArg.userId,
            module: queryArg.module
          }
        }),
        providesTags: ['UserFolders']
      }),
      addUserFolders: build.mutation<
        AddUserFoldersApiResponse,
        AddUserFoldersApiArg
      >({
        query: queryArg => ({
          url: `/userFolders`,
          method: 'POST',
          body: queryArg.createUserFolders
        }),
        invalidatesTags: ['UserFolders']
      }),
      userFolderSearch: build.mutation<
        UserFolderSearchApiResponse,
        UserFolderSearchApiArg
      >({
        query: queryArg => ({
          url: `/userFolderSearch`,
          method: 'POST',
          body: queryArg.userFolderSearch,
          params: { module: queryArg['module'] }
        }),
        invalidatesTags: ['UserFolders']
      }),
      getUserFoldersById: build.query<
        GetUserFoldersByIdApiResponse,
        GetUserFoldersByIdApiArg
      >({
        query: queryArg => ({ url: `/userFolders/${queryArg.id}` }),
        providesTags: ['UserFolders']
      }),
      updateUserFolders: build.mutation<
        UpdateUserFoldersApiResponse,
        UpdateUserFoldersApiArg
      >({
        query: queryArg => ({
          url: `/userFolders/${queryArg.id}`,
          method: 'PUT',
          body: queryArg.updateUserFolders
        }),
        invalidatesTags: ['UserFolders']
      }),
      deleteUserFolders: build.mutation<
        DeleteUserFoldersApiResponse,
        DeleteUserFoldersApiArg
      >({
        query: queryArg => ({
          url: `/userFolders/${queryArg.id}`,
          method: 'DELETE',
          params: { delete: queryArg['delete'] }
        }),
        invalidatesTags: ['UserFolders']
      }),
      addMultipleAssetsToFolder: build.mutation<
        AddMultipleAssetsToFolderApiResponse,
        AddMultipleAssetsToFolderApiArg
      >({
        query: queryArg => ({
          url: `/userFolders/${queryArg.id}/add-assets`,
          method: 'POST',
          body: queryArg.body
        }),
        invalidatesTags: ['UserFolders']
      }),
      bulkDeleteUserFolders: build.mutation<
        BulkDeleteUserFoldersApiResponse,
        BulkDeleteUserFoldersApiArg
      >({
        query: queryArg => ({
          url: `/userFolders/bulk-delete`,
          method: 'POST',
          body: queryArg.body
        }),
        invalidatesTags: ['UserFolders']
      })
    }),
    overrideExisting: false
  });
export { injectedRtkApi as enhancedApi };
export type GetUserFoldersApiResponse = /** status 200 UserFolders found. */ {
  ok?: boolean;
  data?: UserFolders;
  message?: string;
};
export type GetUserFoldersApiArg = {
  /** Search text */
  search?: string;
  /** offset for pagination */
  offset?: number;
  /** number of rows to fetch */
  limit?: number;
  /** module type */
  module?: string;
  /** sort */
  sortby?: string;
  /** order */
  orderby?: string;
  /** userId */
  userId?: string;
};
export type AddUserFoldersApiResponse =
  /** status 201 UserFolders has been created successfully. */ {
    ok?: boolean;
    data?: UserFolders;
    message?: string;
  };
export type AddUserFoldersApiArg = {
  /** UserFolders that needs to be added to the application */
  createUserFolders: CreateUserFolders;
};
export type UserFolderSearchApiResponse = /** status 200 UserFolders found. */ {
  ok?: boolean;
  data?: UserFolders;
  message?: string;
};
export type UserFolderSearchApiArg = {
  /** module type */
  module?: string;
  /** UserFolders that needs to be added to the application */
  userFolderSearch: UserFolderSearch;
};
export type GetUserFoldersByIdApiResponse =
  /** status 200 UserFolders details found. */ {
    ok?: boolean;
    data?: UserFolders;
    message?: string;
  };
export type GetUserFoldersByIdApiArg = {
  /** ID of the UserFolders to retrieve */
  id: string;
};
export type UpdateUserFoldersApiResponse =
  /** status 200 The UserFolders's details has been updated successfully */ {
    ok?: boolean;
    data?: UserFolders;
    message?: string;
  };
export type UpdateUserFoldersApiArg = {
  /** ID of the UserFolders to update */
  id: string;
  /** UserFolders that needs to be added to the application */
  updateUserFolders: UpdateUserFolders;
};
export type DeleteUserFoldersApiResponse =
  /** status 200 The UserFolders's details has been deleted successfully */ {
    ok?: boolean;
    data?: string | null;
    message?: string;
  };
export type DeleteUserFoldersApiArg = {
  /** ID of the UserFolders to delete */
  id: string;
  /** flag to hard delete */
  delete?: string;
};
export type AddMultipleAssetsToFolderApiResponse =
  /** status 200 Assets added to folder. */ {
    ok?: boolean;
    data?: {
      success?: number;
      failed?: number;
    };
    message?: string;
  };
export type AddMultipleAssetsToFolderApiArg = {
  /** ID of the folder */
  id: string;
  /** Array of asset IDs to add to folder */
  body: {
    assetIds?: string[];
  };
};
export type UserFolders = {
  count?: number;
  rows?: {
    id: string;
    name?: string;
    subscriberId?: string;
    status: string;
    filesCount: number;
    createdBy: string;
    createdAt: string;
    updatedAt: string;
    recipeCount: string;
  }[];
};

export type BulkDeleteUserFoldersApiResponse =
  /** status 200 Folders deleted successfully */ {
    ok?: boolean;
    data?: {
      successCount?: number;
      failureCount?: number;
      deletedAssets?: number;
    };
    message?: string;
  };
export type BulkDeleteUserFoldersApiArg = {
  /** Array of folder IDs and delete flag */
  body: {
    folderIds?: string[];
    deleteFlag?: boolean;
  };
};
export type CreateUserFolders = {
  name?: string;
  subscriberId?: string;
  status?: string;
  createdBy?: string;
  module?: string;
};
export type UserFolderSearch = {
  limit?: number;
  offset?: number;
  sortBy?: string;
  orderBy?: string;
  search?: string;
  userId?: string;
  subscriberId?: string;
  favorites?: boolean;
  sharedWith?: string;
  people?: any[];
  things?: any[];
  locations?: any[];
  dates?: any[];
  interactions?: any[];
  mediaType?: any[];
  userFolderId?: string;
  module?: string;
  recipeCount?: string;
  isTrash?: boolean;
};
export type UpdateUserFolders = {
  name?: string;
  subscriberId?: string;
  status?: string;
  createdBy?: string;
  createdAt?: string;
  updatedAt?: string;
  module?: string;
};
export const {
  useGetUserFoldersQuery,
  useAddUserFoldersMutation,
  useUserFolderSearchMutation,
  useGetUserFoldersByIdQuery,
  useUpdateUserFoldersMutation,
  useDeleteUserFoldersMutation,
  useAddMultipleAssetsToFolderMutation,
  useBulkDeleteUserFoldersMutation
} = injectedRtkApi;
